import { useTranslation } from 'next-i18next';
import { useAppDispatch, useAppSelector } from '@/redux';
import { selectShippingList, showProductsPerAvailableTypeModal } from '@/redux/slices/checkout.slice';
import { selectNumberOfProductsPerAvailableType } from '@/redux/slices/cart.slice';
import { useState } from 'react';
import { CartShippingModel } from '@/models';
import { updateCartAttributeByName } from '@/redux/thunks/checkout/updateCartAttributeByName';

const unitMap = {
  M: 'month',
  W: 'week',
  H: 'hour',
  Y: 'year',
  D: 'day'
} as const;

type EnhancedCartShippingModel = CartShippingModel & {
  deliveryTime: { dateL?: string; dateP?: string; time?: number; unit?: keyof typeof unitMap }
};

const availableTypes = {
  L: 'inStock',
  PA: 'prearrival',
  P: 'primeur'
} as const;

type AvailableTypes = keyof typeof availableTypes;
type AvailableTypeWithExclusion<K extends AvailableTypes> = Omit<typeof availableTypes, K>;

export const useShippingList = () => {
  function formatDuration(value: number, unitCode: keyof typeof unitMap, localeOverride = 'fr-FR') {
    if (!value || !unitCode) {
      return;
    }
    const test = new Intl.NumberFormat(localeOverride, {
      style: 'unit',
      unit: unitMap[unitCode],
      unitDisplay: 'long'
    }).format(value);

    return test;
  }

  function addDuration(date: Date, value: number, unit: keyof typeof unitMap): string {
    const newDate = date ? new Date(date) : new Date();
    switch (unit) {
      case 'D':
        newDate.setDate(newDate.getDate() + value);
        break;
      case 'W':
        newDate.setDate(newDate.getDate() + value * 7);
        break;
      case 'M':
        newDate.setMonth(newDate.getMonth() + value);
        break;
      case 'Y':
        newDate.setFullYear(newDate.getFullYear() + value);
        break;
      case 'H':
        newDate.setHours(newDate.getHours() + value);
        break;
      default:
        throw new Error(`Unité non reconnue : ${unit}`);
    }
    const formattedDate = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(newDate);
    return formattedDate;
  }

  function getShippingDeliveryDate(
    shippingList: EnhancedCartShippingModel[], availableType: keyof AvailableTypeWithExclusion<'PA'>
  ) {
    const shipping = shippingList?.find((shipping) => shipping._id === getSelectedShippingIdByAvailableType[availableType]);
    const obj = {
      P: shipping?.deliveryTime?.dateP ? new Date(shipping.deliveryTime.dateP) : null,
      L: shipping?.deliveryTime?.dateL ? new Date(shipping.deliveryTime.dateL) : shipping?.deliveryTime?.unit ?
        addDuration(null, shipping?.deliveryTime?.time, shipping?.deliveryTime?.unit) :
        null,
    };

    const deliveryDate = obj[availableType];

    if (!deliveryDate) {
      return '';
    }

    if (typeof deliveryDate === 'string') {
      return deliveryDate;
    }

    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(deliveryDate);
  }

  function getShippingDeliveryTime(shippingList: EnhancedCartShippingModel[], availableType: AvailableTypes) {
    const shipping = shippingList.find((shipping) => shipping._id === getSelectedShippingIdByAvailableType[availableType]);
    if (!shipping) {
      return;
    }

    let { orderBefore, deliveryBefore } = shipping;
    orderBefore = toDate(orderBefore).toLocaleString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    deliveryBefore = toDate(deliveryBefore)
      .toLocaleString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    return { orderBefore, deliveryBefore };
  }

  function updateShipping({ shippingId, availableType, modifiedShipping }: {
    shippingId?: string, availableType: AvailableTypes, modifiedShipping?: any
  }) {
    const subShippingList: CartShippingModel[] = shippingMap[availableType];
    const shipping = subShippingList.find((shipping) => shipping._id === shippingId);
    const mapAvailableTypeToDispatch = {
      PA: () => dispatch(updateCartAttributeByName({
        attributeName: 'prearrivalShippingMode', newValue: modifiedShipping || shipping
      })),
      L: () => dispatch(updateCartAttributeByName({
        attributeName: 'shippingMode', newValue: modifiedShipping || shipping
      })),
      P: () => dispatch(updateCartAttributeByName({
        attributeName: 'primeurShippingMode', newValue: modifiedShipping || shipping
      }))
    };
    return mapAvailableTypeToDispatch[availableType]();
  }

  function showDeliveryConditions() {
    setUserWantsToSeeDeliveryConditions(true);
  }

  function getLabel(shippingId: AvailableTypes) {
    return t(`checkout:cart.${shippingId}`);
  }

  function getShippingDate(availableType: AvailableTypes, shippingList: EnhancedCartShippingModel[]) {
    if (availableType === 'PA') {
      return getShippingDeliveryTime(shippingList, availableType)?.deliveryBefore || '';
    }
    return getShippingDeliveryDate(shippingList, availableType);
  }

  function getOrderBeforeDate(availableType: AvailableTypes, shippingList: EnhancedCartShippingModel[]) {
    if (availableType === 'PA') {
      const sentence = getShippingDeliveryTime(shippingList, availableType);
      return sentence?.orderBefore;
    }
    return '';
  }

  function showProductsPerAvailableType(
    shippingId: AvailableTypes, shipmentNumber: string, orderBefore: string, deliveryFrom: string
  ) {
    dispatch(showProductsPerAvailableTypeModal({ shouldOpen: true, shippingId, shipmentNumber, orderBefore, deliveryFrom }));
  }

  const toDate = (string: string) => new Date(string);

  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const shippingMap = useAppSelector(selectShippingList);
  const locale = useAppSelector(({ root }) => root.storeConf.locale);
  const selectedShippingId = useAppSelector(({ cart }) => cart.content.shippingMode?._id);
  const prearrivalShippingId = useAppSelector(({ cart }) => cart.content.prearrivalShippingMode?._id);
  const primeurShippingId = useAppSelector(({ cart }) => cart.content.primeurShippingMode?._id);
  const numberOfProductsPerAvailableType = useAppSelector(selectNumberOfProductsPerAvailableType);
  const productPerAvailableTypeModalIsOpened = useAppSelector(({ checkout }) => checkout.productsPerAvailableTypeModal.isOpen);

  const [userWantsToSeeDeliveryConditions, setUserWantsToSeeDeliveryConditions] = useState(false);

  const shippingList = Array.from(Object.entries(shippingMap));

  const getSelectedShippingIdByAvailableType = {
    PA: prearrivalShippingId,
    L: selectedShippingId,
    P: primeurShippingId
  };

  return {
    translate: t,
    locale,
    shippingList,
    getSelectedShippingIdByAvailableType,
    numberOfProductsPerAvailableType,
    userWantsToSeeDeliveryConditions,
    updateShipping,
    showDeliveryConditions,
    getLabel,
    getShippingDate,
    showProductsPerAvailableType,
    productPerAvailableTypeModalIsOpened,
    getOrderBeforeDate,
    shippingMap
  };
};
